import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { Region } from '@6grain/masofa-monolith-api-client/dictionaries/model/region';
import { AnalyticsCatalogService } from '../analytics-catalog.service';
import {
  ReportArtifactItem,
  ReportJobItem,
  ReportTemplateItem,
  isActiveJobStatus
} from '../models/analytics-catalog.models';
import { OrchestratorChatService } from '../orchestrator-chat.service';

interface RegionOption {
  id: string;
  name: string;
}

const CATEGORY_ORDER = ['summary', 'digest', 'analytics', 'results'];

/**
 * Reports tab: template catalog from the design, plus the existing job and file registry.
 */
@Component({
  selector: 'app-reports-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  styleUrls: ['./catalog-tab.shared.scss'],
  template: `
    <section class="catalog-panel">
      <div class="panel-head">
        <div>
          <h2>{{ 'reporting_analytics.reports_catalog_title' | translate }}</h2>
          <span class="muted">{{ 'reporting_analytics.reports_catalog_hint' | translate }}</span>
        </div>
        <div class="filters">
          <input type="search" [(ngModel)]="search" [placeholder]="'reporting_analytics.search_template' | translate" />
          <select [(ngModel)]="category">
            <option value="">{{ 'reporting_analytics.filter_all_categories' | translate }}</option>
            <option *ngFor="let c of categories" [value]="c">{{ 'reporting_analytics.category_' + c | translate }}</option>
          </select>
          <select [(ngModel)]="format">
            <option value="">{{ 'reporting_analytics.filter_all_formats' | translate }}</option>
            <option value="pdf">PDF</option>
            <option value="xlsx">Excel</option>
            <option value="html">HTML</option>
            <option value="pptx">PPTX</option>
          </select>
        </div>
      </div>
      <div class="alert" *ngIf="error">{{ error }}</div>
      <section class="template-group" *ngFor="let group of grouped">
        <h3>{{ 'reporting_analytics.category_' + group.category | translate }}</h3>
        <div class="template-grid">
          <article class="template-card" *ngFor="let card of group.items">
            <h4>{{ titleOf(card) }}</h4>
            <p>{{ descriptionOf(card) }}</p>
            <div class="format-pills">
              <span *ngFor="let f of card.formats">{{ formatLabel(f) }}</span>
            </div>
            <div class="card-actions">
              <button type="button" class="btn primary" (click)="openCreate(card)">{{ 'reporting_analytics.create' | translate }}</button>
              <button type="button" class="btn" (click)="openSubscribe(card)">{{ 'reporting_analytics.subscribe' | translate }}</button>
            </div>
          </article>
        </div>
      </section>
    </section>

    <div class="modal-backdrop" *ngIf="createOpen" (click)="createOpen = false">
      <form class="modal" (click)="$event.stopPropagation()" (ngSubmit)="submitReport(false)">
        <h3>{{ 'reporting_analytics.create_report_title' | translate }}: {{ titleOf(selected!) }}</h3>
        <label>
          {{ 'reporting_analytics.field_template' | translate }}
          <input type="text" [value]="titleOf(selected!)" readonly />
        </label>
        <label>
          {{ 'reporting_analytics.field_region' | translate }} *
          <select name="region" [(ngModel)]="reportForm.geoId" required>
            <option value="">—</option>
            <option *ngFor="let region of regions" [value]="region.id">{{ region.name }}</option>
          </select>
        </label>
        <div class="period-row">
          <label>
            {{ 'reporting_analytics.field_period_from' | translate }} *
            <input type="date" name="from" [(ngModel)]="reportForm.from" required />
          </label>
          <label>
            {{ 'reporting_analytics.field_period_to' | translate }} *
            <input type="date" name="to" [(ngModel)]="reportForm.to" required />
          </label>
        </div>
        <label>
          {{ 'reporting_analytics.field_season_cycle' | translate }}
          <select name="cycle" [(ngModel)]="reportForm.seasonCycle">
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="all">all</option>
          </select>
        </label>
        <div class="format-row">
          <label *ngFor="let f of selected?.formats">
            <input type="radio" name="format" [value]="f" [(ngModel)]="reportForm.format" /> {{ formatLabel(f) }}
          </label>
        </div>
        <label>
          {{ 'reporting_analytics.field_locale' | translate }}
          <select name="locale" [(ngModel)]="reportForm.locale">
            <option value="uz">{{ 'reporting_analytics.locale_uz' | translate }}</option>
            <option value="ru">{{ 'reporting_analytics.locale_ru' | translate }}</option>
          </select>
        </label>
        <p class="preview">{{ previewText() }}</p>
        <div class="alert" *ngIf="formError">{{ formError }}</div>
        <div class="form-actions">
          <button type="button" class="btn" (click)="createOpen = false">{{ 'reporting_analytics.cancel' | translate }}</button>
          <button type="submit" class="btn" [disabled]="saving">{{ 'reporting_analytics.form_report' | translate }}</button>
          <button type="button" class="btn primary" [disabled]="saving" (click)="submitReport(true)">
            {{ 'reporting_analytics.form_and_download' | translate }}
          </button>
        </div>
      </form>
    </div>

    <div class="modal-backdrop" *ngIf="subscribeOpen" (click)="subscribeOpen = false">
      <form class="modal" (click)="$event.stopPropagation()" (ngSubmit)="submitSubscription()">
        <h3>{{ 'reporting_analytics.subscribe_title' | translate }}: {{ titleOf(selected!) }}</h3>
        <label>
          {{ 'reporting_analytics.field_template' | translate }}
          <input type="text" [value]="titleOf(selected!)" readonly />
        </label>
        <label>
          {{ 'reporting_analytics.field_region' | translate }} *
          <select name="subRegion" [(ngModel)]="subForm.geoId" required>
            <option value="">—</option>
            <option *ngFor="let region of regions" [value]="region.id">{{ region.name }}</option>
          </select>
        </label>
        <span class="field-label">{{ 'reporting_analytics.field_schedule' | translate }} *</span>
        <div class="weekday-row">
          <label *ngFor="let day of weekdays">
            <input type="checkbox" [checked]="subForm.days.includes(day.id)" (change)="toggleDay(day.id)" />
            {{ day.label }}
          </label>
        </div>
        <div class="time-row">
          <label>
            {{ 'reporting_analytics.field_time' | translate }}
            <input type="time" name="time" [(ngModel)]="subForm.time" />
          </label>
          <label>
            {{ 'reporting_analytics.cron_or' | translate }}
            <input type="text" name="cron" class="mono" [(ngModel)]="subForm.cron" placeholder="0 4 * * 1" />
          </label>
        </div>
        <span class="field-label">{{ 'reporting_analytics.field_format' | translate }}</span>
        <div class="format-row">
          <label *ngFor="let f of selected?.formats">
            <input type="radio" name="subfmt" [value]="f" [(ngModel)]="subForm.format" /> {{ formatLabel(f) }}
          </label>
        </div>
        <span class="field-label">{{ 'reporting_analytics.field_channel' | translate }}</span>
        <div class="format-row">
          <label><input type="radio" checked disabled /> {{ 'reporting_analytics.channel_file' | translate }}</label>
          <label class="soon"><input type="radio" disabled /> Telegram <small>{{ 'reporting_analytics.soon' | translate }}</small></label>
          <label class="soon"><input type="radio" disabled /> Email <small>{{ 'reporting_analytics.soon' | translate }}</small></label>
        </div>
        <label class="check">
          <input type="checkbox" name="pass" [(ngModel)]="subForm.triggerOnNewPass" />
          {{ 'reporting_analytics.field_trigger_pass' | translate }}
        </label>
        <p class="note">{{ 'reporting_analytics.pass_warning' | translate }}</p>
        <div class="alert" *ngIf="formError">{{ formError }}</div>
        <div class="form-actions">
          <button type="button" class="btn" (click)="subscribeOpen = false">{{ 'reporting_analytics.cancel' | translate }}</button>
          <button type="submit" class="btn primary" [disabled]="saving">{{ 'reporting_analytics.create_subscription' | translate }}</button>
        </div>
      </form>
    </div>

    <section class="catalog-panel list-panel">
      <div class="panel-head">
        <div>
          <h2>{{ 'reporting_analytics.reports_jobs_title' | translate }}</h2>
          <span class="muted">{{ 'reporting_analytics.reports_jobs_hint' | translate }}</span>
        </div>
        <button type="button" class="btn" (click)="loadJobs()" [disabled]="loading">
          <i class="pi pi-refresh" [class.pi-spin]="loading"></i>
          {{ 'reporting_analytics.refresh' | translate }}
        </button>
      </div>
      <div class="catalog-table-wrap" *ngIf="jobs.length; else noJobs">
        <table class="catalog-table">
          <thead>
            <tr>
              <th>{{ 'reporting_analytics.col_title' | translate }}</th>
              <th>{{ 'reporting_analytics.col_format' | translate }}</th>
              <th>{{ 'reporting_analytics.col_status' | translate }}</th>
              <th>{{ 'reporting_analytics.col_created' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let job of pagedJobs">
              <td>{{ job.title || ('reporting_analytics.untitled_report' | translate) }}</td>
              <td>{{ job.format | uppercase }}</td>
              <td>
                <span *ngIf="isActive(job.status)">{{ job.progress_pct }}%</span>
                <span class="status" *ngIf="!isActive(job.status)" [attr.data-status]="job.status">{{ statusKey(job.status) | translate }}</span>
              </td>
              <td class="muted">{{ job.created_at_utc | date: 'dd.MM.yyyy HH:mm' }}</td>
              <td class="actions">
                <button type="button" class="btn icon" *ngIf="job.status === 'done' && job.report_id" (click)="downloadJob(job)">
                  <i class="pi pi-download"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="list-pager">
          <span>{{ 'reporting_analytics.records_total' | translate: { total: jobs.length } }}</span>
          <div class="pager-controls" *ngIf="jobs.length > pageSize">
            <button type="button" (click)="jobsPage = jobsPage - 1" [disabled]="jobsPage <= 1">‹</button>
            <span>{{ jobsPage }} / {{ jobsPageCount }}</span>
            <button type="button" (click)="jobsPage = jobsPage + 1" [disabled]="jobsPage >= jobsPageCount">›</button>
          </div>
        </div>
      </div>
      <ng-template #noJobs>
        <div class="empty" *ngIf="!loading"><p>{{ 'reporting_analytics.no_report_jobs' | translate }}</p></div>
      </ng-template>
    </section>

    <section class="catalog-panel list-panel">
      <div class="panel-head">
        <h2>{{ 'reporting_analytics.reports_files_title' | translate }}</h2>
      </div>
      <div class="catalog-table-wrap" *ngIf="files.length; else noFiles">
        <table class="catalog-table">
          <thead>
            <tr>
              <th>{{ 'reporting_analytics.col_file' | translate }}</th>
              <th>{{ 'reporting_analytics.col_format' | translate }}</th>
              <th>{{ 'reporting_analytics.col_created' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let file of pagedFiles">
              <td>{{ file.title || file.file_name }}</td>
              <td>{{ file.format | uppercase }}</td>
              <td class="muted">{{ file.created_at_utc | date: 'dd.MM.yyyy HH:mm' }}</td>
              <td class="actions">
                <button type="button" class="btn icon" (click)="downloadFile(file)"><i class="pi pi-download"></i></button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="list-pager">
          <span>{{ 'reporting_analytics.records_total' | translate: { total: files.length } }}</span>
          <div class="pager-controls" *ngIf="files.length > pageSize">
            <button type="button" (click)="filesPage = filesPage - 1" [disabled]="filesPage <= 1">‹</button>
            <span>{{ filesPage }} / {{ filesPageCount }}</span>
            <button type="button" (click)="filesPage = filesPage + 1" [disabled]="filesPage >= filesPageCount">›</button>
          </div>
        </div>
      </div>
      <ng-template #noFiles>
        <div class="empty" *ngIf="!loading"><p>{{ 'reporting_analytics.no_report_files' | translate }}</p></div>
      </ng-template>
    </section>
  `
})
export class ReportsTabComponent implements OnInit, OnDestroy {
  templates: ReportTemplateItem[] = [];
  jobs: ReportJobItem[] = [];
  files: ReportArtifactItem[] = [];
  regions: RegionOption[] = [];
  search = '';
  category = '';
  format = '';
  loading = false;
  saving = false;
  error: string | null = null;
  formError: string | null = null;
  createOpen = false;
  subscribeOpen = false;
  selected: ReportTemplateItem | null = null;
  reportForm = this.emptyReport();
  subForm = this.emptySub();
  readonly pageSize = 10;
  jobsPage = 1;
  filesPage = 1;
  readonly categories = CATEGORY_ORDER;
  readonly weekdays = [
    { id: 1, label: 'Пн' },
    { id: 2, label: 'Вт' },
    { id: 3, label: 'Ср' },
    { id: 4, label: 'Чт' },
    { id: 5, label: 'Пт' },
    { id: 6, label: 'Сб' },
    { id: 0, label: 'Вс' }
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly catalog: AnalyticsCatalogService,
    private readonly chatApi: OrchestratorChatService,
    private readonly regionsApi: RegionService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.catalog.listTemplates().pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => (this.templates = items),
      error: (err) => (this.error = this.describe(err))
    });
    this.regionsApi
      .dictionariesRegionGetByQueryPost({
        take: 1000,
        offset: 0,
        filters: [{ filterField: 'level', filterValue: 2, filterOperator: 0 }]
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => (this.regions = this.mapRegions(rows)),
        error: () => (this.regions = [])
      });
    this.loadJobs();
    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.jobs.some((job) => isActiveJobStatus(job.status))) {
          this.loadJobs(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get grouped(): Array<{ category: string; items: ReportTemplateItem[] }> {
    const query = this.search.trim().toLowerCase();
    return CATEGORY_ORDER
      .filter((category) => !this.category || this.category === category)
      .map((category) => ({
        category,
        items: this.templates.filter((card) => {
          if (card.category !== category) {
            return false;
          }
          if (this.format && !card.formats.includes(this.format)) {
            return false;
          }
          if (!query) {
            return true;
          }
          return `${card.title_ru} ${card.title_uz} ${card.description_ru}`.toLowerCase().includes(query);
        })
      }))
      .filter((group) => group.items.length);
  }

  titleOf(card: ReportTemplateItem): string {
    return this.translate.currentLang?.startsWith('uz') ? card.title_uz : card.title_ru;
  }

  descriptionOf(card: ReportTemplateItem): string {
    return this.translate.currentLang?.startsWith('uz') ? card.description_uz : card.description_ru;
  }

  formatLabel(format: string): string {
    return format === 'xlsx' ? 'Excel' : format.toUpperCase();
  }

  isActive(status: string): boolean {
    return isActiveJobStatus(status);
  }

  statusKey(status: string): string {
    return `reporting_analytics.status_${status}`;
  }

  openCreate(card: ReportTemplateItem): void {
    this.selected = card;
    this.formError = null;
    this.reportForm = this.emptyReport();
    this.reportForm.format = card.formats[0];
    this.createOpen = true;
  }

  openSubscribe(card: ReportTemplateItem): void {
    this.selected = card;
    this.formError = null;
    this.subForm = this.emptySub();
    this.subForm.format = card.formats.includes('pdf') ? 'pdf' : card.formats[0];
    this.subscribeOpen = true;
  }

  toggleDay(day: number): void {
    this.subForm.days = this.subForm.days.includes(day)
      ? this.subForm.days.filter((item) => item !== day)
      : [...this.subForm.days, day];
  }

  previewText(): string {
    const region = this.regions.find((item) => item.id === this.reportForm.geoId)?.name || '…';
    return this.translate.instant('reporting_analytics.report_preview', {
      template: this.selected ? this.titleOf(this.selected) : '',
      region,
      from: this.reportForm.from,
      to: this.reportForm.to,
      cycle: this.reportForm.seasonCycle
    });
  }

  submitReport(download: boolean): void {
    if (!this.selected || !this.reportForm.geoId || !this.reportForm.from || !this.reportForm.to) {
      this.formError = this.translate.instant('reporting_analytics.error_report_fields');
      return;
    }
    this.saving = true;
    this.formError = null;
    const region = this.regions.find((item) => item.id === this.reportForm.geoId);
    this.catalog
      .createReport({
        template_id: this.selected.template_id,
        title: this.titleOf(this.selected),
        geo_id: this.reportForm.geoId,
        geo_name: region?.name,
        from: this.reportForm.from,
        to: this.reportForm.to,
        season_cycle: this.reportForm.seasonCycle,
        format: this.reportForm.format,
        locale: this.reportForm.locale
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (job) => {
          this.saving = false;
          this.createOpen = false;
          this.jobs = [job, ...this.jobs];
          this.jobsPage = 1;
          if (download) {
            this.waitAndDownload(job.job_id);
          }
        },
        error: (err) => {
          this.saving = false;
          this.formError = this.describe(err);
        }
      });
  }

  submitSubscription(): void {
    if (!this.selected || !this.subForm.geoId) {
      this.formError = this.translate.instant('reporting_analytics.error_report_fields');
      return;
    }
    if (!this.subForm.cron.trim() && !this.subForm.days.length && !this.subForm.triggerOnNewPass) {
      this.formError = this.translate.instant('reporting_analytics.error_no_trigger');
      return;
    }
    this.saving = true;
    const region = this.regions.find((item) => item.id === this.subForm.geoId);
    this.catalog
      .createSubscription({
        title: this.titleOf(this.selected),
        template_id: this.selected.template_id,
        format: this.subForm.format,
        delivery_channel: 'file',
        geo_id: this.subForm.geoId,
        geo_name: region?.name,
        season_cycle: '1',
        days: this.subForm.cron.trim() ? undefined : this.subForm.days,
        time: this.subForm.cron.trim() ? undefined : this.subForm.time,
        cron: this.subForm.cron.trim() || null,
        trigger_on_new_pass: this.subForm.triggerOnNewPass,
        locale: 'uz'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.subscribeOpen = false;
        },
        error: (err) => {
          this.saving = false;
          this.formError = this.describe(err);
        }
      });
  }

  loadJobs(silent = false): void {
    if (!silent) {
      this.loading = true;
    }
    forkJoin({
      jobs: this.catalog.listJobs({ take: 200 }),
      files: this.catalog.listReports({ take: 200 })
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ jobs, files }) => {
          this.jobs = jobs;
          this.files = files;
          this.jobsPage = Math.min(this.jobsPage, this.pageCount(jobs.length));
          this.filesPage = Math.min(this.filesPage, this.pageCount(files.length));
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.error = this.describe(err);
        }
      });
  }

  get jobsPageCount(): number {
    return this.pageCount(this.jobs.length);
  }

  get filesPageCount(): number {
    return this.pageCount(this.files.length);
  }

  get pagedJobs(): ReportJobItem[] {
    return this.pageSlice(this.jobs, this.jobsPage);
  }

  get pagedFiles(): ReportArtifactItem[] {
    return this.pageSlice(this.files, this.filesPage);
  }

  downloadJob(job: ReportJobItem): void {
    if (job.report_id) {
      this.chatApi.downloadReport(job.report_id, job.title ? `${job.title}.${job.format}` : null).subscribe();
    }
  }

  downloadFile(file: ReportArtifactItem): void {
    if (file.download_url) {
      this.chatApi.downloadUrl(file.download_url, file.file_name).subscribe();
      return;
    }
    this.chatApi.downloadReport(file.report_id, file.file_name).subscribe();
  }

  private waitAndDownload(jobId: string): void {
    const timer = interval(3000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.catalog.getJob(jobId).subscribe({
          next: (job) => {
            if (job.status === 'done' && job.report_id) {
              timer.unsubscribe();
              this.downloadJob(job);
              this.loadJobs(true);
            }
            if (job.status === 'failed') {
              timer.unsubscribe();
              this.error = job.error || this.translate.instant('reporting_analytics.request_failed');
            }
          }
        });
      });
  }

  private pageCount(total: number): number {
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  private pageSlice<T>(items: T[], page: number): T[] {
    const start = (page - 1) * this.pageSize;
    return items.slice(start, start + this.pageSize);
  }

  private mapRegions(rows: Region[] | { items?: Region[] } | null | undefined): RegionOption[] {
    const list = Array.isArray(rows) ? rows : rows?.items ?? [];
    return list
      .map((row) => ({
        id: String(row?.id ?? ''),
        name: row?.names?.['ru-RU'] || row?.names?.['uz-Latn-UZ'] || row?.names?.['en-US'] || String(row?.id ?? '')
      }))
      .filter((row) => row.id)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }

  private describe(err: HttpErrorResponse): string {
    const body = err?.error;
    return (typeof body === 'object' && (body?.error || body?.message)) || err?.message || 'Request failed';
  }

  private emptyReport() {
    const today = new Date().toISOString().slice(0, 10);
    return { geoId: '', from: today, to: today, seasonCycle: '1', format: 'pdf', locale: 'uz' };
  }

  private emptySub() {
    return { geoId: '', days: [1] as number[], time: '09:00', cron: '', triggerOnNewPass: false, format: 'pdf' };
  }
}
